import { useCallback, useEffect, useReducer, useRef, type RefObject } from "react";

export type ShellPanel = "command" | "inbox" | "help";

type ShellPanelAction =
  | { type: "open"; panel: ShellPanel }
  | { type: "toggle"; panel: ShellPanel }
  | { type: "close"; panel: ShellPanel };

const OWN_ESCAPE_LAYERS = '[role="dialog"],[role="alertdialog"],[role="menu"],[role="listbox"]';

function shellPanelReducer(open: ShellPanel | null, action: ShellPanelAction) {
  switch (action.type) {
    case "open":
      return action.panel;
    case "toggle":
      return open === action.panel ? null : action.panel;
    case "close":
      return open === action.panel ? null : open;
  }
}

function currentFocus() {
  return document.activeElement instanceof HTMLElement ? document.activeElement : null;
}

export function useShellShortcuts(inboxRef: RefObject<HTMLElement | null>) {
  const [open, dispatch] = useReducer(shellPanelReducer, null);
  const trigger = useRef<HTMLElement | null>(null);

  const openPanel = useCallback((panel: ShellPanel) => {
    trigger.current ??= currentFocus();
    dispatch({ type: "open", panel });
  }, []);

  const togglePanel = useCallback((panel: ShellPanel) => {
    trigger.current ??= currentFocus();
    dispatch({ type: "toggle", panel });
  }, []);

  const closePanel = useCallback((panel: ShellPanel) => {
    dispatch({ type: "close", panel });
    const element = trigger.current;
    trigger.current = null;
    if (element?.isConnected) element.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        togglePanel("command");
        return;
      }
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const target = event.target;
      if (!(target instanceof Element) || !inboxRef.current?.contains(target)) return;
      if (target.closest(OWN_ESCAPE_LAYERS)) return;
      closePanel("inbox");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closePanel, inboxRef, togglePanel]);

  return { open, openPanel, togglePanel, closePanel };
}
